import BeenhereIcon from '@mui/icons-material/Beenhere';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { Alert, Box, Button, Container, Typography } from '@mui/material';
import downloadjs from 'downloadjs';
import { useCallback, useContext, useEffect } from 'react';
import CoursesApi from '../../../../../apis/CoursesApi';
import Logo from '../../../../../components/Logo/Logo';
import SnackbarContext from '../../../../../contexts/SnackbarContext';
import useApi from '../../../../../lib/useApi';
import BaseModuleProps from '../../../lib/BaseModuleProps';

/**
 * The properties for the complete module
 */
interface CompleteModuleProps extends BaseModuleProps {
  /**
   * The id of the course that's complete
   */
  courseId: string;
}

/**
 * A module to show when the training is complete
 */
const CompleteModule = (props: CompleteModuleProps) => {
  const coursesApi = useApi(CoursesApi);
  const snackbar = useContext(SnackbarContext);

  useEffect(() => {
    props.setComplete();
  }, [props]);

  /**
   * Generate the certificate for the user
   */
  const generateCertificate = useCallback(() => {
    coursesApi
      .GetCertificate(props.courseId)
      .then((r) => {
        downloadjs(r.content, r.filename);
      })
      .catch((e) => {
        console.error(e);
        snackbar.setAlert(
          <Alert variant="filled" severity="error">
            An error occurred while generating the certificate. Please try
            again. If this continues, contact us.
          </Alert>
        );
        snackbar.triggerSnackbar();
      });
  }, [coursesApi, props.courseId, snackbar]);

  return (
    <Container
      data-testid="complete-module-id"
      maxWidth="md"
      sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
    >
      <Box display="flex" flexDirection="column">
        <Box
          marginBottom="2em"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h2">Course Complete</Typography>
          <BeenhereIcon
            color="success"
            sx={{ fontSize: '3em', marginRight: '.5em', marginTop: '.25em' }}
          />
        </Box>
        <Typography marginBottom="2em">
          Congratulations on reaching the end of the course! We hope the time
          you&apos;ve spent learning with us has been both rewarding and
          insightful. The knowledge you&apos;ve gained is a testament to your
          dedication and curiosity. We encourage you to keep pursuing new
          knowledge and challenges, and we&apos;re here to help you every step
          of the way.
        </Typography>
        <Typography marginBottom="4em">
          Your journey doesn&apos;t end here — thank you for letting us be a
          part of it.
        </Typography>
        <Box display="flex" justifyContent="center" gap="2em">
          <Logo sizeEm={2} showText />
          <Button
            data-testid="certificate-download-button-id"
            variant="contained"
            endIcon={<DownloadRoundedIcon />}
            onClick={generateCertificate}
          >
            Download Certificate
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default CompleteModule;
